import { makeAutoObservable, runInAction } from 'mobx'
import type { Notify } from '@/Ship/Contracts/Notify'
import { parseApiError } from '@/Ship/Helpers/parseApiError'
import { InviteUserTask } from '../Tasks/InviteUserTask'
import type { InviteUserPayload } from '../Contracts/InvitationPayload'

/**
 * Local store of `InvitationUserDrawer`.
 *
 * Owns the drawer's open/loading/success flags. Form values themselves
 * live in `Core/UI/Form` formContext (created in the view), so the store
 * receives them via `submit(values, ...)`.
 *
 * The drawer is opened from `UserPage` via `crudContext.setState('openInviteDrawer', true)`
 * — this store does not own the open flag (it's a list-context state).
 */
export class InvitationUserStore {
    loading = false
    successSubmit = false

    constructor() {
        makeAutoObservable(this)
    }

    resetSuccess(): void {
        this.successSubmit = false
    }

    /**
     * Normalize a form-collected position value (could be an object reference)
     * into the IRI string the backend expects.
     */
    private normalizePosition(value: any): string | null {
        if (value && typeof value === 'object') return value['@id'] ?? null
        return value ?? null
    }

    /**
     * Submit invitation form values.
     * Returns `true` on success — the view should close the drawer and reload list.
     * On validation 422 — extracts `violations` and feeds them to `formContext.setError`.
     */
    async submit(values: Record<string, any>, formContext: any, notify: Notify): Promise<boolean> {
        this.loading = true
        try {
            const payload: InviteUserPayload = {
                lastName: values['lastName'],
                firstName: values['firstName'],
                middleName: values['middleName'] || null,
                phone: values['phone'],
                email: values['email'],
                roles: values['roles'],
                position: this.normalizePosition(values['position']),
            }
            await InviteUserTask(payload)
            notify.success('Користувача успішно запрошено')
            runInAction(() => {
                this.successSubmit = true
            })
            return true
        } catch (err: any) {
            const parsed = await parseApiError(err)
            if (parsed.violations?.length) {
                parsed.violations.forEach((v) => formContext.setError(v.field, v.message))
                return false
            }
            notify.error(parsed.message ?? 'Не вдалося надіслати запрошення')
            return false
        } finally {
            runInAction(() => {
                this.loading = false
            })
        }
    }
}
